/**
 * US state-level cosmetic substance evaluator.
 *
 * Evaluates a formula's ingredients against:
 *  • California Proposition 65 (warning required)
 *  • CA SB-484 Toxic-Free Cosmetics Act (banned, effective 2025-01-01)
 *  • WA HB 1047 (banned, effective 2025-01-01)
 *  • NY S4265B (1,4-dioxane disclosure / 1 ppm limit)
 *  • MD HB643 (banned, effective 2025-01-01)
 */

import stateRules from "../data/stateRules.json";

const normalizeCas = (value) =>
  String(value || "").trim().toLowerCase().replace(/\s+/g, "").replace(/^n\/a$/, "");

const splitCas = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((entry) => String(entry || "").split(/\s*,\s*/))
    .map(normalizeCas)
    .filter(Boolean);

const ingredientCasSet = (ingredients = []) => {
  const set = new Set();
  ingredients.forEach((ingredient) => {
    splitCas(ingredient.cas_number).forEach((cas) => set.add(cas));
  });
  return set;
};

const findSubstanceMatches = (substanceList, casSet) =>
  substanceList.filter((s) => casSet.has(normalizeCas(s.cas)));

export const evaluateStateLabelRules = ({ ingredients = [], markets = [] } = {}) => {
  const casSet = ingredientCasSet(ingredients);
  const findings = [];

  // California Prop 65 — applies whenever distributed to California.
  // Conservative default: include if "US" or "CA" is in markets, or if not
  // specified at all (treat as "may be sold nationwide").
  const caApplies = markets.length === 0 || markets.some((m) => /us|ca/i.test(m));

  if (caApplies) {
    const propMatches = findSubstanceMatches(stateRules.californiaProp65.substances, casSet);
    propMatches.forEach((substance) => {
      findings.push({
        code: "CA_PROP_65_WARNING",
        state: "CA",
        ruleSet: stateRules.californiaProp65.shortName,
        substance: substance.name,
        cas: substance.cas,
        severity: "warning",
        blocking: false,
        text: stateRules.californiaProp65.warningTemplate
          .replace("{substance}", substance.name)
          .replace("{harm}", substance.harm),
        citation: stateRules.californiaProp65.citation,
        citationUrl: stateRules.californiaProp65.sourceUrl,
      });
    });

    const sb484Matches = findSubstanceMatches(stateRules.californiaSb484.substances, casSet);
    sb484Matches.forEach((substance) => {
      findings.push({
        code: "CA_SB_484_PROHIBITED",
        state: "CA",
        ruleSet: stateRules.californiaSb484.shortName,
        substance: substance.name,
        cas: substance.cas,
        severity: "critical",
        blocking: true,
        text: stateRules.californiaSb484.warningTemplate.replace("{substance}", substance.name),
        citation: stateRules.californiaSb484.citation,
        citationUrl: stateRules.californiaSb484.sourceUrl,
      });
    });
  }

  // Washington
  if (markets.length === 0 || markets.some((m) => /us|wa/i.test(m))) {
    const waMatches = findSubstanceMatches(stateRules.washington.substances, casSet);
    waMatches.forEach((substance) => {
      findings.push({
        code: "WA_HB1047_PROHIBITED",
        state: "WA",
        ruleSet: stateRules.washington.shortName,
        substance: substance.name,
        cas: substance.cas,
        severity: "critical",
        blocking: true,
        text: `Banned in Washington cosmetic products effective ${stateRules.washington.effectiveDate}: ${substance.name}.`,
        citation: stateRules.washington.citation,
        citationUrl: stateRules.washington.sourceUrl,
      });
    });
  }

  // New York 1,4-Dioxane (concentration limit)
  if (markets.length === 0 || markets.some((m) => /us|ny/i.test(m))) {
    const nyMatches = findSubstanceMatches(stateRules.newYork.substances, casSet);
    nyMatches.forEach((substance) => {
      findings.push({
        code: "NY_S4265B_LIMIT",
        state: "NY",
        ruleSet: stateRules.newYork.shortName,
        substance: substance.name,
        cas: substance.cas,
        severity: "high",
        blocking: true,
        text: `${substance.name} is limited to ${substance.limitPpm} ppm in cosmetic products distributed in New York.`,
        citation: stateRules.newYork.citation,
        citationUrl: stateRules.newYork.sourceUrl,
      });
    });
  }

  // Maryland
  if (markets.length === 0 || markets.some((m) => /us|md/i.test(m))) {
    const mdMatches = findSubstanceMatches(stateRules.maryland.substances, casSet);
    mdMatches.forEach((substance) => {
      findings.push({
        code: "MD_HB643_PROHIBITED",
        state: "MD",
        ruleSet: stateRules.maryland.shortName,
        substance: substance.name,
        cas: substance.cas,
        severity: "critical",
        blocking: true,
        text: `Banned in Maryland cosmetic products effective ${stateRules.maryland.effectiveDate}: ${substance.name}.`,
        citation: stateRules.maryland.citation,
        citationUrl: stateRules.maryland.sourceUrl,
      });
    });
  }

  return findings;
};

export const getStateRuleMetadata = () => ({
  californiaProp65: stateRules.californiaProp65.shortName,
  californiaSb484: stateRules.californiaSb484.shortName,
  washington: stateRules.washington.shortName,
  newYork: stateRules.newYork.shortName,
  maryland: stateRules.maryland.shortName,
});
